import { v4 as uuidv4 } from 'uuid';
import { Element, View } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export interface ElevationGeometryData {
    position: [number, number, number];
    rotation: number; // Angle in radians
    viewId: string;
}

export interface ElevationElement extends Element {
    category: 'Views';
    geometryData: ElevationGeometryData;
}

export class ElevationTool extends BaseTool {
    id = 'elevation';
    name = 'Elevation';
    cursor = 'crosshair';

    private currentPoint: THREE.Vector3 | null = null;
    private rotation: number = 0;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);
        this.createElevation(snapped);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);

            // Auto-rotate based on nearby walls?
            // For now, just default to 0 (North) or allow manual rotation later.
            // Let's implement simple wall detection logic later.

            this.updatePreview();
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.context?.appStore.setToolPreview(null);
        } else if (event.key === ' ') {
            // Rotate on Spacebar
            this.rotation = (this.rotation + Math.PI / 2) % (Math.PI * 2);
            this.updatePreview();
        } else {
            super.onKeyDown(event);
        }
    }

    private updatePreview() {
        if (!this.context || !this.currentPoint) return;

        this.context.appStore.setToolPreview({
            type: 'elevation',
            position: this.currentPoint,
            rotation: this.rotation
        });
    }

    private createElevation(position: THREE.Vector3) {
        if (!this.context) return;

        const viewId = uuidv4();
        const elevationId = uuidv4();

        // Calculate Look Direction
        const direction = new THREE.Vector3(0, 1, 0).applyAxisAngle(new THREE.Vector3(0, 0, 1), this.rotation);

        // Create the View
        const newView: View = {
            id: viewId,
            name: `Elevation ${this.context.bimStore.project.views.size + 1}`,
            type: 'Elevation',
            scale: 100,
            detailLevel: 'Fine',
            visibility: {},
            filters: [],
            camera: {
                // Stand back from the placement point along the opposite of
                // the look direction so the orthographic side view frames the
                // model; target sits at the marker looking in `direction`.
                position: [
                    position.x - direction.x * 30,
                    position.y - direction.y * 30,
                    position.z + 2.5,
                ],
                target: [position.x + direction.x, position.y + direction.y, position.z + 2.5],
                projection: 'Orthographic'
            }
            // Elevations usually don't have clipping planes by default, or they have a far clip.
        };

        // Create the Elevation Symbol
        const elevationElement: ElevationElement = {
            id: elevationId,
            category: 'Views',
            type: 'Elevation Marker',
            parameters: {
                'View Name': newView.name,
                'View Id': viewId
            },
            constraints: [],
            geometryData: {
                position: [position.x, position.y, position.z],
                rotation: this.rotation,
                viewId: viewId
            }
        };

        // Register the view and the marker element through the store so the
        // Project Browser and viewport pick up both immediately.
        this.context.bimStore.addView(newView);
        this.context.bimStore.addElement(elevationElement);
    }
}
